import { ArrowLeft, Shield, Heart, Database, Lock, Globe, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  const lastUpdated = "February 6, 2025";

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to App
        </Link>

        <div className="space-y-8">
          {/* Header */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Privacy Policy</h1>
                <p className="text-sm text-muted-foreground">Last updated: {lastUpdated}</p>
              </div>
            </div>
            <p className="text-muted-foreground">
              Evolve AI Coach ("Evolve", "we", "us", or "our") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, and safeguard your information when you use our mobile application.
            </p>
          </div>

          {/* HealthKit Section - Critical for App Store Compliance */}
          <section className="bg-red-500/5 border border-red-500/20 rounded-xl p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">Apple HealthKit Data</h2>
            </div>
            
            <p className="text-muted-foreground">
              Evolve integrates with Apple HealthKit to provide personalized fitness coaching. 
              This section specifically addresses how we handle your HealthKit data in compliance with Apple's guidelines.
            </p>

            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-foreground mb-2">Data We Access</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 flex-shrink-0" />
                    <span><strong>Body Weight</strong> – Read and write weight measurements to track your progress</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 flex-shrink-0" />
                    <span><strong>Step Count</strong> – Read daily steps to display activity on your dashboard</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 flex-shrink-0" />
                    <span><strong>Heart Rate</strong> – Read heart rate data for workout insights</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 flex-shrink-0" />
                    <span><strong>Active Energy</strong> – Read and write calorie expenditure for progress tracking</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 flex-shrink-0" />
                    <span><strong>Distance</strong> – Read walking/running distance for activity display</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 flex-shrink-0" />
                    <span><strong>Nutrition Data</strong> – Write dietary information (protein, carbs, fat, calories) when you log meals</span>
                  </li>
                </ul>
                <p className="text-sm text-muted-foreground mt-3 italic">
                  Note: Any data written to Apple Health (HealthKit) occurs only when the user explicitly logs or confirms information in the app. The app does not automatically create, modify, infer, or update HealthKit records without direct user action.
                </p>
              </div>

              <div className="bg-background/50 rounded-lg p-4 space-y-3">
                <h3 className="font-medium text-foreground flex items-center gap-2">
                  <Lock className="w-4 h-4 text-green-500" />
                  Our Privacy Commitments for HealthKit Data
                </h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2 text-muted-foreground">
                    <span className="text-green-500 font-bold">✓</span>
                    <span><strong>On-Device Processing:</strong> All HealthKit data is processed entirely on your device. Raw health metrics are never transmitted to external servers.</span>
                  </li>
                  <li className="flex items-start gap-2 text-muted-foreground">
                    <span className="text-green-500 font-bold">✓</span>
                    <span><strong>No Advertising:</strong> HealthKit data is never used for advertising, marketing, or any form of user tracking.</span>
                  </li>
                  <li className="flex items-start gap-2 text-muted-foreground">
                    <span className="text-green-500 font-bold">✓</span>
                    <span><strong>No Third-Party Sharing:</strong> We do not share, sell, or transfer your HealthKit data to any third parties, including analytics providers, data brokers, or advertisers.</span>
                  </li>
                  <li className="flex items-start gap-2 text-muted-foreground">
                    <span className="text-green-500 font-bold">✓</span>
                    <span><strong>Fitness Coaching Only:</strong> HealthKit data is used solely to personalize your in-app fitness coaching experience, display your progress, and sync workout results.</span>
                  </li>
                  <li className="flex items-start gap-2 text-muted-foreground">
                    <span className="text-green-500 font-bold">✓</span>
                    <span><strong>No SDK Access:</strong> No analytics SDKs, advertising SDKs, or third-party tracking libraries have access to your HealthKit data.</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-medium text-foreground mb-2">Managing HealthKit Permissions</h3>
                <p className="text-sm text-muted-foreground">
                  You can revoke HealthKit access at any time through your device's Settings app under Privacy & Security → Health → Evolve. 
                  Revoking access will not delete any data already stored in the app, but will stop future synchronization.
                </p>
              </div>
            </div>
          </section>

          {/* Health Connect (Android) Section */}
          <section className="bg-green-500/5 border border-green-500/20 rounded-xl p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">Google Health Connect Data</h2>
            </div>
            
            <p className="text-muted-foreground">
              On Android devices, Evolve integrates with Health Connect. The same privacy commitments that apply to 
              Apple HealthKit also apply to Health Connect data. Your health information is processed on-device, 
              never shared with third parties, and used exclusively for fitness coaching within the app.
            </p>
          </section>

          {/* AI Data Processing - Required compliance section */}
          <section className="bg-primary/5 border border-primary/20 rounded-xl p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Globe className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">AI Data Processing</h2>
            </div>
            
            <p className="text-muted-foreground">
              When you use AI-powered features, the app collects user-entered text and relevant usage context. 
              This information is securely transmitted to a third-party AI service (OpenAI) for the sole purpose 
              of generating personalized responses. The app does not sell user data. The AI service provider is 
              required to provide equal or stronger data protection and does not use this data for advertising purposes.
            </p>

            <div className="space-y-3">
              <h3 className="font-medium text-foreground">Additional Details</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <span>AI data is transmitted to the third-party AI service only after the user explicitly consents in the app, and no AI requests occur before consent is granted. This consent can be managed in your app settings.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <span>Data sent includes your messages and relevant context (e.g., fitness goals, workout history) to provide personalized coaching.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                  <span><strong>Important:</strong> HealthKit and Health Connect data is never sent to the AI service. All health data remains on your device.</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Data We Collect */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Database className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">Other Data We Collect</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-foreground mb-2">Account Information</h3>
                <p className="text-sm text-muted-foreground">
                  When you create an account, we collect your email address and any profile information you choose to provide 
                  (name, profile picture, fitness goals, physical stats).
                </p>
              </div>

              <div>
                <h3 className="font-medium text-foreground mb-2">Fitness Data</h3>
                <p className="text-sm text-muted-foreground">
                  We store workout sessions, nutrition logs, weight history, and personal records you enter in the app. 
                  This data is linked to your account and used to provide personalized coaching.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-foreground mb-2">Usage Data</h3>
                <p className="text-sm text-muted-foreground">
                  We collect anonymous usage analytics to improve the app experience. This does not include HealthKit data or personal health information.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-foreground mb-2">Push Notifications</h3>
                <p className="text-sm text-muted-foreground">
                  If you enable push notifications, we use OneSignal to send workout reminders and updates. 
                  OneSignal receives only a non-health, non-profile device identifier used solely for notification delivery and does not have access to your health data, personal information, or any HealthKit data.
                </p>
              </div>
            </div>
          </section>

          {/* Data Retention & Deletion */}
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-destructive" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">Data Retention & Deletion</h2>
            </div>
            
            <p className="text-muted-foreground">
              We retain your data for as long as your account is active. You can delete your account and all associated data at any time 
              through the app's Settings menu or by visiting our{" "}
              <Link to="/delete-account" className="text-primary hover:underline">
                account deletion page
              </Link>.
            </p>
            
            <p className="text-muted-foreground">
              Upon account deletion, all your personal data including workout history, chat messages, nutrition logs, 
              and profile information will be permanently removed from our servers.
            </p>
          </section>

          {/* Contact */}
          <section className="border-t border-border pt-6 space-y-3">
            <h2 className="text-xl font-semibold text-foreground">Contact Us</h2>
            <p className="text-muted-foreground">
              If you have questions about this Privacy Policy or our data practices, please contact us at{" "}
              <a 
                href="mailto:support@evolvewithcoach.com" 
                className="text-primary hover:underline"
              >
                support@evolvewithcoach.com
              </a>
            </p>
          </section>

          {/* Additional Legal Links */}
          <section className="border-t border-border pt-6 space-y-3">
            <h2 className="text-lg font-semibold text-foreground">Additional Legal Documents</h2>
            <div className="flex flex-col gap-2">
              <a 
                href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline text-sm"
              >
                Terms of Use (EULA)
              </a>
              <Link to="/delete-account" className="text-primary hover:underline text-sm">
                Account Deletion Instructions
              </Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
